// Dracula-flavored tokenizer for the embed snippet. Permissive — not a real
// HTML/JS parser, just enough pattern-matching to color the canonical
// snippet shape nicely in the EMBED CODE panel.
//
// Returns React-friendly token segments: callers map tokens to spans with
// inline color styles. Keeping this file framework-agnostic so the JSX
// layer stays thin.

import type { ReactNode } from 'react'
import { Fragment, createElement } from 'react'

const PINK = '#ff79c6'
const CYAN = '#8be9fd'
const GREEN = '#50fa7b'
const PURPLE = '#bd93f9'
const COMMENT = '#6272a4'
const FG = '#f8f8f2'

interface Token {
  text: string
  color?: string
}

function tokenize(line: string): Token[] {
  const out: Token[] = []
  let i = 0
  let buf = ''
  const flush = () => { if (buf) { out.push({ text: buf }); buf = '' } }
  const push = (text: string, color: string) => { flush(); out.push({ text, color }) }

  while (i < line.length) {
    const ch = line[i]!

    // HTML tag open
    const tagOpen = line.slice(i).match(/^<\/?([a-zA-Z][a-zA-Z0-9-]*)/)
    if (tagOpen) {
      push(tagOpen[0][0]!, COMMENT)
      if (tagOpen[0][1] === '/')
        push('/', COMMENT)
      push(tagOpen[1]!, PINK)
      i += tagOpen[0].length

      while (i < line.length && line[i] !== '>') {
        const ws = line.slice(i).match(/^\s+/)
        if (ws) { buf += ws[0]; i += ws[0].length; continue }
        if (line[i] === '/' && line[i + 1] === '>') { push('/', COMMENT); i += 1; continue }
        const attr = line.slice(i).match(/^([a-zA-Z_:][a-zA-Z0-9_:.-]*)/)
        if (attr) {
          push(attr[1]!, CYAN)
          i += attr[1]!.length
          if (line[i] === '=') { push('=', COMMENT); i += 1 }
          if (line[i] === '"' || line[i] === '\'') {
            const q = line[i]!
            const end = line.indexOf(q, i + 1)
            const valEnd = end === -1 ? line.length : end + 1
            push(line.slice(i, valEnd), GREEN)
            i = valEnd
          }
          continue
        }
        buf += line[i]!
        i += 1
      }
      if (line[i] === '>') { push('>', COMMENT); i += 1 }
      continue
    }

    // Quoted string
    if (ch === '\'' || ch === '"') {
      const end = line.indexOf(ch, i + 1)
      const valEnd = end === -1 ? line.length : end + 1
      push(line.slice(i, valEnd), GREEN)
      i = valEnd
      continue
    }

    // Identifier
    const ident = line.slice(i).match(/^[A-Za-z_$][A-Za-z0-9_$]*/)
    if (ident) {
      const word = ident[0]
      const next = line[i + word.length]
      if (next === '(')
        push(word, GREEN)
      else if (next === ':')
        push(word, CYAN)
      else if (line[i - 1] === '.')
        push(word, CYAN)
      else
        push(word, FG)
      i += word.length
      continue
    }

    // Number
    const num = line.slice(i).match(/^\d+(\.\d+)?/)
    if (num) { push(num[0], PURPLE); i += num[0].length; continue }

    // Punctuation
    if ('{}()[],;:.'.includes(ch)) { push(ch, COMMENT); i += 1; continue }

    buf += ch
    i += 1
  }
  flush()
  return out
}

export function highlightLine(line: string, key: number): ReactNode {
  const tokens = tokenize(line)
  if (tokens.length === 0)
    return createElement(Fragment, { key }, ' ')
  return createElement(
    Fragment,
    { key },
    tokens.map((t, i) =>
      t.color
        ? createElement('span', { key: i, style: { color: t.color } }, t.text)
        : t.text,
    ),
  )
}
